import Holidays from 'date-holidays';
import { DateTime } from 'luxon';
export function dateFromStringOrDate(value) {
    return typeof value == 'string' ? new Date(value) : value;
}
export function jsDateToLocalDateString(date) {
    const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    const year = localDate.getFullYear();
    const month = String(localDate.getMonth() + 1).padStart(2, '0');
    const day = String(localDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}
export function getRelativeTime(date, displayType = 'Relative') {
    const dateTime = typeof date == 'string' ? DateTime.fromISO(date) : date instanceof DateTime ? date : DateTime.fromJSDate(date);
    const now = DateTime.now();
    const diffInSeconds = Math.floor((now.toMillis() - dateTime.toMillis()) / 1000);
    const absDiffInSeconds = Math.abs(diffInSeconds);
    // Pro RelativeDate typ - vracíme jednoduché české názvy dnů
    if (displayType === 'RelativeDate') {
        const nowDate = now.startOf('day');
        const inputDate = dateTime.startOf('day');
        const daysDiff = Math.floor((inputDate.toMillis() - nowDate.toMillis()) / (1000 * 60 * 60 * 24));
        if (daysDiff === 0) {
            return 'dnes';
        }
        else if (daysDiff === 1) {
            return 'zítra';
        }
        else if (daysDiff === 2) {
            return 'pozítří';
        }
        else if (daysDiff === -1) {
            return 'včera';
        }
        else {
            // Pro ostatní dny použijeme standardní formát
            const weekday = dateTime.toFormat('cccc', { locale: 'cs' });
            return `${weekday} ${dateTime.toFormat('d. M. yyyy')}`;
        }
    }
    const rtf = new Intl.RelativeTimeFormat('cs', { numeric: 'auto' });
    let relativeTime;
    if (absDiffInSeconds < 60) {
        relativeTime = rtf.format(-diffInSeconds, 'second');
    }
    else if (absDiffInSeconds < 3600) {
        relativeTime = rtf.format(-Math.floor(diffInSeconds / 60), 'minute');
    }
    else if (absDiffInSeconds < 86400) {
        relativeTime = rtf.format(-Math.floor(diffInSeconds / 3600), 'hour');
    }
    else {
        const daysDiff = Math.floor(diffInSeconds / 86400);
        relativeTime = rtf.format(-daysDiff, 'day');
        if (Math.abs(daysDiff) <= 1) {
            relativeTime += ` v ${dateTime.toFormat('HH:mm')}`;
        }
    }
    // Přidání datumu podle typu zobrazení
    if (displayType === 'RelativeWithDate' && absDiffInSeconds >= 86400) {
        const dayOfWeek = dateTime.toFormat('cccc', { locale: 'cs' });
        const dateFormatted = dateTime.toFormat('d. M.');
        relativeTime += `, ${dayOfWeek} ${dateFormatted}`;
    }
    return relativeTime;
}
export function validateAndParseDate(inputDate) {
    if (inputDate instanceof Date) {
        return inputDate;
    }
    const formats = ['d.M.yyyy', 'dd.MM.yyyy', 'd.MM.yyyy', 'dd.M.yyyy', 'M/d/yyyy', 'MM/dd/yyyy', 'M/dd/yyyy', 'MM/d/yyyy'];
    inputDate = inputDate.trim().replace(/\s+/g, '');
    for (const format of formats) {
        const parsed = DateTime.fromFormat(inputDate, format);
        if (parsed.isValid) {
            return parsed.toJSDate();
        }
    }
    return null;
}
export function validateAndParseDateRange(inputRange) {
    const [fromInput, toInput] = inputRange.split('-').map(str => str.trim());
    const fromDate = validateAndParseDate(fromInput);
    const toDate = validateAndParseDate(toInput);
    return { start: fromDate, end: toDate };
}
export function parseDateShortcutExpression(input) {
    const now = new Date();
    const daysOfWeek = ['ne', 'po', 'ut', 'st', 'ct', 'pa', 'so'];
    const relativeRegex = /^([+-]?)(\d*)([dtrm])$/;
    const absoluteRegex = /^(\d{2})(\d{2})?(\d{2})?$/;
    const dayOfWeekRegex = /^(po|ut|st|ct|pa|so|ne)$/;
    const monthEndRegex = /^km(\d{2})?$/;
    const monthStartRegex = /^zm(\d{2})?$/;
    const relativeDayOfWeekRegex = /^([+-])(po|ut|st|ct|pa|so|ne)$/;
    const relativeMonthEndRegex = /^([+-]?)(\d*)km$/;
    const relativeMonthStartRegex = /^([+-]?)(\d*)zm$/;
    if (input == 'd') {
        now.setHours(12, 0, 0, 0);
    }
    else if (input == 'z') {
        now.setDate(now.getDate() + 1);
        now.setHours(12, 0, 0, 0);
    }
    else if (input == 'v') {
        now.setDate(now.getDate() - 1);
        now.setHours(12, 0, 0, 0);
    }
    else if (relativeRegex.test(input)) {
        const [, sign, value, unit] = relativeRegex.exec(input);
        const amount = parseInt(value || '1', 10);
        const isPositive = sign !== '-';
        const delta = isPositive ? amount : -amount;
        switch (unit) {
            case 'd':
                now.setDate(now.getDate() + delta);
                break;
            case 't':
                now.setDate(now.getDate() + delta * 7);
                break;
            case 'm':
                now.setMonth(now.getMonth() + delta);
                break;
            case 'r':
                now.setFullYear(now.getFullYear() + delta);
                break;
        }
    }
    else if (absoluteRegex.test(input)) {
        const [, day, month, year] = absoluteRegex.exec(input);
        const targetYear = year ? parseInt('20' + year, 10) : now.getFullYear();
        const targetMonth = month ? parseInt(month, 10) - 1 : now.getMonth();
        const targetDay = parseInt(day, 10);
        now.setFullYear(targetYear);
        now.setMonth(targetMonth);
        now.setDate(targetDay);
        now.setHours(12, 0, 0, 0);
    }
    else if (dayOfWeekRegex.test(input)) {
        const targetDayIndex = daysOfWeek.indexOf(input);
        const todayIndex = now.getDay();
        const deltaDays = (targetDayIndex - todayIndex + 7) % 7;
        if (deltaDays === 0) {
            now.setDate(now.getDate() + 7);
        }
        else {
            now.setDate(now.getDate() + deltaDays);
        }
    }
    else if (monthEndRegex.test(input)) {
        const [, month] = monthEndRegex.exec(input);
        const targetMonth = month ? parseInt(month, 10) - 1 : now.getMonth();
        const lastDay = new Date(now.getFullYear(), targetMonth + 1, 0);
        now.setFullYear(lastDay.getFullYear());
        now.setMonth(lastDay.getMonth());
        now.setDate(lastDay.getDate());
    }
    else if (relativeMonthEndRegex.test(input)) {
        const [, sign, value] = relativeMonthEndRegex.exec(input);
        const amount = parseInt(value || '1', 10);
        const isPositive = sign !== '-';
        const delta = isPositive ? amount : -amount;
        now.setMonth(now.getMonth() + delta);
        const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        now.setFullYear(lastDay.getFullYear());
        now.setMonth(lastDay.getMonth());
        now.setDate(lastDay.getDate());
    }
    else if (monthStartRegex.test(input)) {
        const [, month] = monthStartRegex.exec(input);
        const targetMonth = month ? parseInt(month, 10) - 1 : now.getMonth();
        now.setFullYear(now.getFullYear());
        now.setMonth(targetMonth);
        now.setDate(1);
        now.setHours(12, 0, 0, 0);
    }
    else if (relativeMonthStartRegex.test(input)) {
        const [, sign, value] = relativeMonthStartRegex.exec(input);
        const amount = parseInt(value || '1', 10);
        const isPositive = sign !== '-';
        const delta = isPositive ? amount : -amount;
        now.setMonth(now.getMonth() + delta);
        now.setDate(1);
    }
    else if (relativeDayOfWeekRegex.test(input)) {
        const [, sign, dayOfWeek] = relativeDayOfWeekRegex.exec(input);
        const targetDayIndex = daysOfWeek.indexOf(dayOfWeek);
        const todayIndex = now.getDay();
        let deltaDays;
        if (sign === '+') {
            deltaDays = (targetDayIndex - todayIndex + 7) % 7;
            if (deltaDays === 0) {
                deltaDays = 7;
            }
        }
        else {
            deltaDays = (todayIndex - targetDayIndex + 7) % 7;
            if (deltaDays === 0) {
                deltaDays = 7;
            }
            deltaDays = -deltaDays;
        }
        now.setDate(now.getDate() + deltaDays);
    }
    else {
        return null;
    }
    return now;
}
export function parseShortcutDateExpressionRange(input) {
    const [fromInput, toInput] = input.split(':').map(str => str.trim());
    const fromDate = fromInput ? parseDateShortcutExpression(fromInput) : new Date();
    const toDate = toInput ? parseDateShortcutExpression(toInput) : new Date();
    return { start: fromDate, end: toDate };
}
export function formatDateInLocal(date) {
    const locale = navigator.language;
    const options = {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric'
    };
    const formatter = new Intl.DateTimeFormat(locale, options);
    return formatter.format(date);
}
export function getCurrentDateTime() {
    return DateTime.now().toJSDate();
}
export function getCurrentTimeOfDayInSeconds(dateTime) {
    const date = dateTime ? DateTime.fromJSDate(dateTime) : DateTime.fromJSDate(getCurrentDateTime());
    return date.toSeconds() - date.startOf('day').toSeconds();
}
export function isWeekend(date) {
    const dateObj = DateTime.fromJSDate(date);
    return dateObj.weekday == 6 || dateObj.weekday == 7;
}
export function isHoliday(date) {
    const dateObj = DateTime.fromJSDate(date);
    const holiday = new Holidays({ languages: ['cs'], timezone: 'Europe/Prague', types: ['public'] });
    return holiday.isHoliday(dateObj.toJSDate());
}
export function getStartOfGivenDateOrNextWorkday(date) {
    let nextDay = DateTime.fromJSDate(date).startOf('day');
    while (isWeekend(nextDay.toJSDate()) || isHoliday(nextDay.toJSDate())) {
        nextDay = nextDay.plus({ days: 1 });
    }
    return nextDay.toJSDate();
}
export function secondsToTime(seconds, format = 'HH:mm') {
    if (seconds == null) {
        return null;
    }
    const hours = String(Math.floor(seconds / 3600)).padStart(2, '0');
    const minutes = String(Math.floor((seconds % 3600) / 60)).padStart(2, '0');
    const remainingSeconds = String(seconds % 60).padStart(2, '0');
    return format === 'HH:mm' ? `${hours}:${minutes}` : `${hours}:${minutes}:${remainingSeconds}`;
}
export function timeToSeconds(time) {
    if (time == null) {
        return null;
    }
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 3600 + minutes * 60;
}
export function parseTimeInputValueToSeconds(timeValue) {
    if (timeValue) {
        const [hours, minutes] = timeValue.split(':');
        const hoursNumber = Number.parseInt(hours);
        const minutesNumber = Number.parseInt(minutes);
        return (hoursNumber * 60 + minutesNumber) * 60;
    }
    return null;
}
export function formatSecondsToTimeInputValue(seconds) {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const result = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    return result;
}
//# sourceMappingURL=date.util.js.map